import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronDown } from 'lucide-react';

const SearchableDropdown = ({
  label,
  placeholder = "Search...",
  selected,
  onSelect,
  fetchDefault,
  fetchSearch,
  renderItem,
  disabled = false,
  required = false,
  error = null,
  className = "",
  value = "",
  onChange = null,
  onBlur = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDefault, setShowDefault] = useState(true);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Fetch default results when dropdown opens
  useEffect(() => {
    if (isOpen && showDefault && fetchDefault) {
      setLoading(true);
      fetchDefault()
        .then((data) => {
          setResults(data || []);
          setLoading(false);
        })
        .catch((error) => {
          setResults([]);
          setLoading(false);
        });
    }
  }, [isOpen, showDefault, fetchDefault]);

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;

    if (searchTerm.trim().length >= 2) {
      setShowDefault(false);
      setLoading(true);

      const handler = setTimeout(() => {
        if (fetchSearch) {
          fetchSearch(searchTerm.trim())
            .then((data) => {
              setResults(data || []);
              setLoading(false);
            })
            .catch((error) => {
              setResults([]);
              setLoading(false);
            });
        }
      }, 300);

      return () => clearTimeout(handler);
    } else if (searchTerm.trim().length === 0) {
      // Show default results when search is cleared
      setShowDefault(true);
      if (fetchDefault) {
        setLoading(true);
        fetchDefault()
          .then((data) => {
            setResults(data || []);
            setLoading(false);
          })
          .catch((error) => {
            setResults([]);
            setLoading(false);
          });
      }
    }
  }, [searchTerm, isOpen, fetchSearch, fetchDefault]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm("");
        setShowDefault(true);
        if (onBlur) {
          onBlur();
        }
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchTerm("");
        setShowDefault(true);
        if (inputRef.current) {
          inputRef.current.blur();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleInputFocus = () => {
    // If an item is selected, clear it on focus to allow searching
    if (selected) {
      onSelect(null);
      setSearchTerm("");
      setShowDefault(true);
      // Small delay to ensure state updates
      setTimeout(() => {
        setIsOpen(true);
        if (inputRef.current) {
          inputRef.current.select();
        }
      }, 10);
    } else {
      setIsOpen(true);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (onChange) {
      onChange(e);
    }
  };

  const handleSelect = (item) => {
    onSelect(item);
    setIsOpen(false);
    setSearchTerm("");
    setShowDefault(true);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onSelect(null);
    setSearchTerm("");
    setResults([]);
    setIsOpen(false);
    setShowDefault(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Get display value
  const getDisplayValue = () => {
    if (selected) {
      if (renderItem) {
        const rendered = renderItem(selected, true);
        // renderItem should return a string when isSelected=true
        if (typeof rendered === 'string') {
          return rendered;
        }
      }
      // Fallback to common field names
      return selected.companyName || selected.name || `${selected.firstName || ''} ${selected.lastName || ''}`.trim() || selected.label || '';
    }
    return searchTerm || value || "";
  };

  const displayValue = getDisplayValue();

  return (
    <div className={`relative w-full ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* Input wrapper with relative positioning */}
      <div className="relative w-full">
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 pr-20 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent transition-colors ${error ? 'border-red-300' : isOpen ? 'border-green-400' : 'border-gray-300'
            } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''} ${selected ? 'bg-gray-50 cursor-pointer' : ''}`}
          readOnly={selected ? true : false}
        />
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1 pointer-events-none">
          {selected && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 pointer-events-auto"
              tabIndex={-1}
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
        </div>

        {/* Dropdown - positioned absolutely below input */}
        {isOpen && !disabled && (
          <div
            ref={dropdownRef}
            className="absolute left-0 top-[calc(100%+6px)] w-full bg-white rounded-lg shadow-lg border border-gray-200 z-[99999] max-h-60 overflow-y-auto"
            style={{
              zIndex: 99999,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}
          >
            {loading ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                Loading...
              </div>
            ) : results.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                {searchTerm.trim().length >= 2 ? 'No results found' : 'Start typing to search...'}
              </div>
            ) : (
              <div className="py-1">
                {results.map((item, index) => (
                  <div
                    key={item._id || item.id || index}
                    onClick={() => handleSelect(item)}
                    className="px-3 py-3 hover:bg-gray-100 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
                  >
                    {renderItem ? renderItem(item, false) : (
                      <div>
                        <div className="font-medium text-gray-900">
                          {item.name || item.label || item.title || 'Unnamed'}
                        </div>
                        {item.description && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {item.description}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="text-xs text-red-600 mt-1">{error}</p>
      )}
    </div>
  );
};

export default SearchableDropdown;
